import { LeaveAreaCode } from '@gb-ticket/shared-types';
import { useState } from 'react';
import { Link, useParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { PersonChip } from '@/platform/components/person-chip';
import { leavePanelClassName } from '@/products/leave/features/leave-panel';
import { useMe } from '@/platform/auth/use-me';
import { leaveAreaEnabled, useLeaveAreas } from '@/products/leave/features/use-leave-areas';
import { formatLeaveDays } from '@/products/leave/features/preview-vacation-days';
import {
  formatLeaveRange,
  LEAVE_EVENT_LABEL,
  LEAVE_REQUEST_STATUS_LABEL,
} from '@/products/leave/features/leave-labels';
import {
  useCancelLeaveRequest,
  useDecideLeaveRequest,
  useLeaveRequest,
  useLeaveRequestEvents,
} from '@/products/leave/features/use-leave';
import { LEAVE_INBOX_PATH, LEAVE_REQUESTS_PATH } from '@/products/leave/leave-paths';

export function LeaveRequestDetailPage() {
  const { id = '' } = useParams();
  const me = useMe();
  const areas = useLeaveAreas();
  const capacityOn = leaveAreaEnabled(areas.data, LeaveAreaCode.CAPACITY) !== false;
  const request = useLeaveRequest(id);
  const events = useLeaveRequestEvents(id);
  const decide = useDecideLeaveRequest();
  const cancel = useCancelLeaveRequest();
  const [decisionNote, setDecisionNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  usePageTitle(
    request.data
      ? `Antrag ${formatLeaveRange(request.data.startsOn, request.data.endsOn)}`
      : 'Antrag',
  );

  const isRequester = request.data?.requester.id === me.data?.id;
  const canDecide =
    request.data?.status === 'submitted' &&
    !isRequester &&
    me.data?.id !== request.data.requester.id;

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Urlaubsantrag"
        description={
          request.data ? formatLeaveRange(request.data.startsOn, request.data.endsOn) : undefined
        }
      />
      <p>
        <Link to={LEAVE_REQUESTS_PATH} className="text-sm text-primary hover:underline">
          ← Meine Anträge
        </Link>
        {' · '}
        <Link to={LEAVE_INBOX_PATH} className="text-sm text-primary hover:underline">
          Eingang
        </Link>
      </p>
      {request.isPending || events.isPending ? (
        <p className="text-sm text-muted-foreground">Antrag wird geladen …</p>
      ) : null}
      {request.isError ? (
        <p className="text-sm text-destructive">
          {request.error instanceof ApiRequestError
            ? request.error.message
            : 'Der Antrag konnte nicht geladen werden.'}
        </p>
      ) : null}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {request.data ? (
        <section className={`${leavePanelClassName} space-y-4`}>
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground">Art</dt>
              <dd className="font-medium">{request.data.absenceType.labelDe}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Status</dt>
              <dd className="font-medium">{LEAVE_REQUEST_STATUS_LABEL[request.data.status]}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Tage</dt>
              <dd className="font-medium">{formatLeaveDays(request.data.days)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Antragsteller</dt>
              <dd className="font-medium">
                <PersonChip
                  displayName={request.data.requester.displayName}
                  profilePhotoUrl={request.data.requester.profilePhotoUrl}
                />
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Vertretung</dt>
              <dd className="font-medium">{request.data.substitute?.displayName ?? '—'}</dd>
            </div>
            {request.data.comment ? (
              <div className="sm:col-span-2">
                <dt className="text-muted-foreground">Hinweis</dt>
                <dd>{request.data.comment}</dd>
              </div>
            ) : null}
            {request.data.decidedBy ? (
              <>
                <div>
                  <dt className="text-muted-foreground">Entschieden von</dt>
                  <dd className="font-medium">{request.data.decidedBy.displayName}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Entschieden am</dt>
                  <dd className="font-medium">
                    {request.data.decidedAt ? formatDateTime(request.data.decidedAt) : '—'}
                  </dd>
                </div>
              </>
            ) : null}
            {request.data.decisionNote ? (
              <div className="sm:col-span-2">
                <dt className="text-muted-foreground">Begründung</dt>
                <dd>{request.data.decisionNote}</dd>
              </div>
            ) : null}
          </dl>
          {request.data.warnings?.substitutionOtherDepartment ? (
            <p className="text-sm text-amber-700 dark:text-amber-300">
              Hinweis: Die Vertretung gehört zu einer anderen Abteilung.
            </p>
          ) : null}
          {capacityOn
            ? request.data.warnings?.capacityUnderMin?.map((warning) => (
                <p key={warning.date} className="text-sm text-amber-700 dark:text-amber-300">
                  Besetzungswarnung am{' '}
                  {new Intl.DateTimeFormat('de-DE').format(new Date(`${warning.date}T12:00:00`))}:
                  nur {warning.present} von mindestens {warning.minPresent} Personen anwesend.
                </p>
              ))
            : null}
          {isRequester &&
          (request.data.status === 'draft' || request.data.status === 'submitted') ? (
            <Button
              type="button"
              variant="outline"
              disabled={cancel.isPending}
              onClick={() => {
                setError(null);
                void cancel.mutateAsync(id).catch((caught: unknown) => {
                  setError(
                    caught instanceof ApiRequestError
                      ? caught.message
                      : 'Zurückziehen ist fehlgeschlagen.',
                  );
                });
              }}
            >
              Zurückziehen
            </Button>
          ) : null}
          {canDecide ? (
            <div className="space-y-3 border-t pt-4">
              <div>
                <Label htmlFor="decision-note">Begründung bei Ablehnung</Label>
                <textarea
                  id="decision-note"
                  className="mt-2 min-h-20 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                  value={decisionNote}
                  onChange={(event) => setDecisionNote(event.target.value)}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  disabled={decide.isPending}
                  onClick={() => {
                    setError(null);
                    void decide
                      .mutateAsync({ id, body: { decision: 'approved' } })
                      .catch((caught: unknown) => {
                        setError(
                          caught instanceof ApiRequestError
                            ? caught.message
                            : 'Genehmigen ist fehlgeschlagen.',
                        );
                      });
                  }}
                >
                  Genehmigen
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={decide.isPending}
                  onClick={() => {
                    setError(null);
                    void decide
                      .mutateAsync({
                        id,
                        body: { decision: 'rejected', decisionNote: decisionNote.trim() || null },
                      })
                      .catch((caught: unknown) => {
                        setError(
                          caught instanceof ApiRequestError
                            ? caught.message
                            : 'Ablehnen ist fehlgeschlagen.',
                        );
                      });
                  }}
                >
                  Ablehnen
                </Button>
              </div>
            </div>
          ) : null}
        </section>
      ) : null}
      {events.data?.items.length ? (
        <section className={`${leavePanelClassName} space-y-4`}>
          <h2 className="text-lg font-semibold">Verlauf</h2>
          <ol className="relative space-y-4 border-l pl-6">
            {events.data.items.map((event) => (
              <li key={event.id} className="relative">
                <span className="absolute top-1 -left-[1.35rem] size-2.5 rounded-full bg-primary" />
                <p className="font-medium">{LEAVE_EVENT_LABEL[event.type]}</p>
                <p className="text-sm text-muted-foreground">
                  {event.actor.displayName} · {formatDateTime(event.at)}
                </p>
              </li>
            ))}
          </ol>
        </section>
      ) : null}
    </div>
  );
}
